import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './schema'

/**
 * Fired whenever any request comes back 401 - the session cookie is missing or expired.
 * The auth context subscribes to this to bounce the whole app back to the login screen,
 * rather than every call site having to check response.status itself.
 */
export type UnauthorizedListener = () => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

const authMiddleware: Middleware = {
  async onResponse({ response, request }) {
    // The login POST is expected to 401 on a wrong password - that's not a "your session
    // died" event, so it must not trigger the global bounce-to-login behavior.
    if (response.status === 401 && !request.url.endsWith('/session')) {
      for (const listener of unauthorizedListeners) listener()
    }
    return response
  },
}

// The session cookie is HttpOnly (by design - JS can't read it, only the browser can send
// it), so every request must explicitly opt in to sending cookies cross-request.
export const api = createClient<paths>({ baseUrl: '/api', credentials: 'include' })
api.use(authMiddleware)
